// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

const CACHE_PREFIX = 'cv_cache_';
const CACHE_INDEX_KEY = 'cv_cache__index';
const DEFAULT_TTL_SECONDS = 3600; // 1 hour
const MAX_CACHE_BYTES = 5 * 1024 * 1024; // 5MB

interface CacheEntry<T = unknown> {
  data: T;
  expiresAt: number; // unix timestamp in ms
  storedAt: number;
}

/** Get the set of tracked cache keys from the index. */
function getIndex(): Set<string> {
  try {
    const raw = localStorage.getItem(CACHE_INDEX_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

/** Persist the cache key index. */
function saveIndex(index: Set<string>): void {
  localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify([...index]));
}

/** Add a key to the index. */
function addToIndex(key: string): void {
  const index = getIndex();
  index.add(key);
  saveIndex(index);
}

/** Remove a key from the index. */
function removeFromIndex(key: string): void {
  const index = getIndex();
  index.delete(key);
  saveIndex(index);
}

/**
 * Store an API response in localStorage with a TTL.
 * Evicts oldest entries if total cache size exceeds MAX_CACHE_BYTES.
 */
export function cacheApiResponse<T>(key: string, data: T, ttlSeconds: number = DEFAULT_TTL_SECONDS): void {
  const storageKey = CACHE_PREFIX + key;
  const entry: CacheEntry<T> = {
    data,
    expiresAt: Date.now() + ttlSeconds * 1000,
    storedAt: Date.now(),
  };

  try {
    const serialized = JSON.stringify(entry);
    localStorage.setItem(storageKey, serialized);
    addToIndex(storageKey);
    evictIfOverSize();
  } catch {
    // Storage full or unavailable — evict and retry once
    try {
      clearExpiredCache();
      evictOldest();
      const serialized = JSON.stringify(entry);
      localStorage.setItem(storageKey, serialized);
      addToIndex(storageKey);
    } catch {
      // Give up silently
    }
  }
}

/**
 * Retrieve a cached API response if it exists and has not expired.
 * Returns null if missing or expired.
 */
export function getCachedResponse<T = unknown>(key: string): T | null {
  const storageKey = CACHE_PREFIX + key;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(storageKey);
      removeFromIndex(storageKey);
      return null;
    }

    return entry.data;
  } catch {
    // Corrupted data — remove it
    localStorage.removeItem(storageKey);
    removeFromIndex(storageKey);
    return null;
  }
}

/**
 * Clear all cached API responses. Non-cache localStorage keys are preserved.
 */
export function clearCache(): void {
  const index = getIndex();
  for (const key of index) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(CACHE_INDEX_KEY);
}

/**
 * Remove only expired cache entries.
 */
export function clearExpiredCache(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];

  for (const key of getIndex()) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        keysToRemove.push(key);
        continue;
      }
      const entry: CacheEntry = JSON.parse(raw);
      if (now > entry.expiresAt) {
        keysToRemove.push(key);
      }
    } catch {
      keysToRemove.push(key);
    }
  }

  const index = getIndex();
  keysToRemove.forEach((k) => {
    localStorage.removeItem(k);
    index.delete(k);
  });
  saveIndex(index);
}

/** Calculate approximate total size of all cache entries in bytes. */
function getCacheSizeBytes(): number {
  let total = 0;
  for (const key of getIndex()) {
    const value = localStorage.getItem(key);
    if (value) {
      total += key.length * 2 + value.length * 2; // UTF-16
    }
  }
  return total;
}

/** Evict the oldest cache entry. */
function evictOldest(): void {
  let oldestKey: string | null = null;
  let oldestTime = Infinity;

  for (const key of getIndex()) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const entry: CacheEntry = JSON.parse(raw);
      if (entry.storedAt < oldestTime) {
        oldestTime = entry.storedAt;
        oldestKey = key;
      }
    } catch {
      // Corrupted — evict immediately
      localStorage.removeItem(key);
      removeFromIndex(key);
      return;
    }
  }

  if (oldestKey) {
    localStorage.removeItem(oldestKey);
    removeFromIndex(oldestKey);
  }
}

/** Evict oldest entries until cache is under MAX_CACHE_BYTES. */
function evictIfOverSize(): void {
  let attempts = 0;
  while (getCacheSizeBytes() > MAX_CACHE_BYTES && attempts < 20) {
    evictOldest();
    attempts++;
  }
}
